import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchSubscriptionDetails } from "../../../../service/billing/subscriptionService";
import { fetchInvoices } from "../../../../service/billing/invoiceService";
import { fetchPlanDetails } from "../../../../service/billing/planService";
import {
  formatCurrency as formatCurrencyHelper,
  mapInvoiceCollection,
  mapSubscriptionResponse,
  mapSubscriptionSummary,
} from "../../../../helpers/billingHelpers";
import { getBillingStatus } from "../../../../service/app/version";

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const DEFAULT_CURRENCY = "CAD";

const resolvePlanId = (plan) => plan?.id ?? plan?.priceId ?? null;

const useBillingSettings = (initialPlans = []) => {
  const initialPlansRef = useRef(initialPlans);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFirstTime, setIsFirstTime] = useState(true);
  const [wizardActive, setWizardActive] = useState(false);
  const [plans, setPlans] = useState(initialPlansRef.current);
  const [selectedPlanId, setSelectedPlanId] = useState(() => {
    const defaultPlan =
      initialPlansRef.current.find((plan) => plan.isDefault) ??
      initialPlansRef.current[0];
    return resolvePlanId(defaultPlan);
  });
  const [userCount, setUserCount] = useState(1);
  const [currency, setCurrency] = useState(DEFAULT_CURRENCY);
  const [existingBilling, setExistingBilling] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [invoicesError, setInvoicesError] = useState(null);
  const [billingData, setBillingData] = useState({});

  const isMountedRef = useRef(true);
  const subscriptionStatusSyncRef = useRef(null);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleSubscriptionResponse = useCallback((response) => {
    const {
      firstTime,
      summary,
      currency: resolvedCurrency,
    } = mapSubscriptionResponse(response, DEFAULT_CURRENCY);

    if (isMountedRef.current) {
      setIsFirstTime(firstTime);
      setExistingBilling(summary);
      setCurrency(resolvedCurrency);
    }

    return { firstTime, summary, currency: resolvedCurrency };
  }, []);

  const handleInvoicesResponse = useCallback(
    (response) => {
      const normalized = mapInvoiceCollection(response, currency);
      setInvoices(normalized);
      return normalized;
    },
    [currency]
  );

  const handlePlanResponse = useCallback(({ plans, estimatedUsers } = {}) => {
    const normalized = Array.isArray(plans) ? plans : [];
    setPlans(normalized);
    setUserCount(estimatedUsers || 1);
    return normalized;
  }, []);

  const handleBillingDataResponse = useCallback((response) => {
    setBillingData(response);
    return response;
  });

  const loadSubscription = useCallback(() => fetchSubscriptionDetails(), []);
  const loadInvoices = useCallback(() => fetchInvoices(), []);
  const loadPlans = useCallback(() => fetchPlanDetails(), []);
  const loadBillingData = useCallback(() => getBillingStatus(), []);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      setError(null);

      try {
        const [billingDataResponse] = await Promise.all([loadBillingData()]);
        const [subscriptionResponse, planResponse] = await Promise.all([
          loadSubscription(),
          loadPlans(),
        ]);
        if (cancelled) {
          return;
        }
        handleBillingDataResponse(billingDataResponse);
        handlePlanResponse(planResponse);
        handleSubscriptionResponse(subscriptionResponse);
      } catch (fetchError) {
        if (cancelled) {
          return;
        }

        setError(fetchError);
        setIsFirstTime(true);
        setExistingBilling(null);
        setCurrency(DEFAULT_CURRENCY);
        setPlans(initialPlansRef.current);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [
    handlePlanResponse,
    handleSubscriptionResponse,
    loadPlans,
    loadSubscription,
  ]);

  useEffect(() => {
    if (isFirstTime) {
      setInvoices([]);
      setInvoicesError(null);
      setInvoicesLoading(false);
      return;
    }

    let cancelled = false;

    const run = async () => {
      setInvoicesLoading(true);
      setInvoicesError(null);

      try {
        const response = await loadInvoices();
        if (cancelled) {
          return;
        }

        handleInvoicesResponse(response);
      } catch (fetchError) {
        if (cancelled) {
          return;
        }

        setInvoicesError(fetchError);
        setInvoices([]);
      } finally {
        if (!cancelled) {
          setInvoicesLoading(false);
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [handleInvoicesResponse, isFirstTime, loadInvoices]);

  useEffect(() => {
    setSelectedPlanId((previous) => {
      if (previous && plans.some((plan) => resolvePlanId(plan) === previous)) {
        return previous;
      }

      const defaultPlan = plans[0];
      return resolvePlanId(defaultPlan);
    });
  }, [plans]);

  const selectedPlan = useMemo(
    () => plans.find((plan) => resolvePlanId(plan) === selectedPlanId) ?? null,
    [plans, selectedPlanId]
  );

  const formatCurrency = useCallback(
    (value, overrideCurrency) =>
      formatCurrencyHelper(
        value,
        (overrideCurrency ?? currency) || DEFAULT_CURRENCY
      ),
    [currency]
  );

  const handlePlanHighlight = useCallback((planId) => {
    setSelectedPlanId(planId);
  }, []);

  const handlePlanConfirm = useCallback((planId) => {
    setSelectedPlanId(planId);
    setWizardActive(true);
  }, []);

  const handleWizardCancel = useCallback(() => {
    setWizardActive(false);
  }, []);

  const handleWizardComplete = useCallback(
    (summary) => {
      if (!summary) {
        return;
      }

      const normalized = mapSubscriptionSummary(
        { ...summary, currency: summary.currency ?? currency },
        currency || DEFAULT_CURRENCY
      );

      setExistingBilling(normalized);
      if (normalized?.currency) {
        setCurrency(normalized.currency);
      }
    },
    [currency]
  );

  const handleReturnToStatus = useCallback(() => {
    setIsFirstTime(false);
    setWizardActive(false);
  }, []);

  const refreshInvoices = useCallback(async () => {
    setInvoicesLoading(true);
    setInvoicesError(null);

    try {
      const response = await loadInvoices();
      handleInvoicesResponse(response);
      return response;
    } catch (fetchError) {
      setInvoicesError(fetchError);
      setInvoices([]);
      throw fetchError;
    } finally {
      setInvoicesLoading(false);
    }
  }, [handleInvoicesResponse, loadInvoices]);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [subscriptionResponse, planResponse] = await Promise.all([
        loadSubscription(),
        loadPlans(),
      ]);
      handlePlanResponse(planResponse);
      const result = handleSubscriptionResponse(subscriptionResponse);

      if (!result.firstTime) {
        await refreshInvoices();
      } else {
        setInvoices([]);
      }

      return subscriptionResponse;
    } catch (fetchError) {
      setError(fetchError);
      setIsFirstTime(true);
      setExistingBilling(null);
      setCurrency(DEFAULT_CURRENCY);
      setInvoices([]);
      setPlans(initialPlansRef.current);
      throw fetchError;
    } finally {
      setLoading(false);
    }
  }, [
    handlePlanResponse,
    handleSubscriptionResponse,
    loadPlans,
    loadSubscription,
    refreshInvoices,
  ]);

  const waitForSubscriptionUpdate = useCallback(
    async (predicate, options = {}) => {
      const { pollIntervalMs = 3000, timeoutMs = 45000 } = options;
      const deadline = Date.now() + timeoutMs;
      let lastSummary = existingBilling;

      while (Date.now() < deadline) {
        const response = await loadSubscription();
        const result = handleSubscriptionResponse(response);

        if (typeof predicate === "function") {
          if (predicate(result.summary, lastSummary)) {
            return result.summary;
          }
        } else if (result.summary !== lastSummary) {
          return result.summary;
        }

        lastSummary = result.summary;

        if (!isMountedRef.current) {
          return result.summary;
        }

        await sleep(pollIntervalMs);
      }

      const timeoutError = new Error(
        "Timed out waiting for subscription update"
      );
      timeoutError.code = "SUBSCRIPTION_SYNC_TIMEOUT";
      throw timeoutError;
    },
    [existingBilling, handleSubscriptionResponse, loadSubscription]
  );

  useEffect(() => {
    if (!existingBilling || isFirstTime) {
      subscriptionStatusSyncRef.current = null;
      return;
    }

    const normalizedStatus = String(existingBilling.status ?? "").toLowerCase();
    if (normalizedStatus !== "incomplete") {
      subscriptionStatusSyncRef.current = null;
      return;
    }

    const subscriptionId =
      existingBilling.subscriptionId ?? "__unknown_subscription__";

    if (subscriptionStatusSyncRef.current === subscriptionId) {
      return;
    }

    subscriptionStatusSyncRef.current = subscriptionId;

    waitForSubscriptionUpdate(
      (summary) => {
        if (!summary) {
          return false;
        }

        if (
          subscriptionId &&
          summary.subscriptionId &&
          summary.subscriptionId !== subscriptionId
        ) {
          return false;
        }

        const status = String(summary.status ?? "").toLowerCase();
        return Boolean(status) && status !== "incomplete";
      },
      { pollIntervalMs: 4000, timeoutMs: 60000 }
    )
      .catch(() => {})
      .finally(() => {
        if (subscriptionStatusSyncRef.current === subscriptionId) {
          subscriptionStatusSyncRef.current = null;
        }
      });

    return () => {
      if (subscriptionStatusSyncRef.current === subscriptionId) {
        subscriptionStatusSyncRef.current = null;
      }
    };
  }, [existingBilling, isFirstTime, waitForSubscriptionUpdate]);

  return {
    loading,
    error,
    isFirstTime,
    billingData,
    wizardActive,
    selectedPlanId,
    selectedPlan,
    userCount,
    setUserCount,
    existingBilling,
    plans,
    currency,
    invoices,
    invoicesLoading,
    invoicesError,
    formatCurrency,
    handlePlanHighlight,
    handlePlanConfirm,
    handleWizardCancel,
    handleWizardComplete,
    handleReturnToStatus,
    refresh,
    refreshInvoices,
    waitForSubscriptionUpdate,
  };
};

export default useBillingSettings;
